import * as React from 'react';
import { cva, VariantProps } from 'class-variance-authority';
import { cn } from '../../utils/cn';
import { OmitStylesTextareaHTMLAttributes } from '../../types';

const textareaVariants = cva(
    'flex min-h-24 w-full rounded-2 text-[16px] ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-base',
    {
        variants: {
            text: {
                default: '',
                medium: 'font-medium',
            },
            color: {
                default:
                    'border border-input bg-background placeholder:text-muted-foreground',
                lightOutline:
                    'border border-gray-200 bg-white text-gray-900 placeholder:text-gray-500/70',
                error: 'border-red-500',
                contrast:
                    'border border-white bg-white text-gray-900 placeholder:text-gray-900/50',
                gray: 'border border-transparent bg-gray-100 text-gray-900 placeholder:text-gray-900/50',
            },
            spacing: {
                default: 'px-3 py-2',
                medium: 'px-4 py-3',
            },
            focusStyle: {
                default:
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                none: 'focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent focus-visible:ring-offset-0',
            },
        },
        defaultVariants: {
            text: 'default',
            color: 'default',
            spacing: 'default',
            focusStyle: 'default',
        },
    }
);

export interface TextareaProps
    extends OmitStylesTextareaHTMLAttributes<HTMLTextAreaElement>,
        VariantProps<typeof textareaVariants> {}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
    ({ text, color, spacing, className, focusStyle, ...props }, ref) => {
        return (
            <textarea
                className={cn(
                    textareaVariants({
                        text,
                        color,
                        spacing,
                        focusStyle,
                        className,
                    })
                )}
                ref={ref}
                data-slot="textarea"
                {...props}
            />
        );
    }
);

Textarea.displayName = 'Textarea';
